import React, { useEffect, useState } from "react";
import { ArrowUpRight, Newspaper } from "@phosphor-icons/react";
import SEO, { articleJsonLd } from "../components/SEO";

const API = process.env.REACT_APP_BACKEND_URL;

// Themed fallback images for items whose RSS doesn't include one.
// All URLs verified live (200 OK) — chosen for cream/bronze warmth.
const FALLBACK_IMAGES = [
  "https://images.unsplash.com/photo-1584982751601-97dcc096659c?w=800&h=500&fit=crop&auto=format", // consultation
  "https://images.unsplash.com/photo-1571019613454-1cb2f99b2d8b?w=800&h=500&fit=crop&auto=format", // runner morning
  "https://images.unsplash.com/photo-1512621776951-a57141f2eefd?w=800&h=500&fit=crop&auto=format", // salad
  "https://images.unsplash.com/photo-1505751172876-fa1923c5c528?w=800&h=500&fit=crop&auto=format", // stethoscope + notebook
  "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=500&fit=crop&auto=format", // laboratory
  "https://images.unsplash.com/photo-1461896836934-ffe607ba8211?w=800&h=500&fit=crop&auto=format", // runner golden hour
];

function pickFallback(idx) {
  return FALLBACK_IMAGES[Math.abs(idx) % FALLBACK_IMAGES.length];
}

function formatDate(iso) {
  if (!iso) return null;
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return null;
  }
}

export default function Blog() {
  const [items, setItems] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API}/api/journal/items`);
        if (!r.ok) throw new Error("failed");
        const data = await r.json();
        if (!cancelled) setItems(data.items || []);
      } catch {
        if (!cancelled) setError(true);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="blog-page">
      <SEO
        title="Journal · Heart health & cardiometabolic news"
        description="Curated cardiovascular, hypertension and cardiometabolic news — rewritten in plain English by our consultants for our patients."
        path="/blog"
        jsonLd={
          items && items.length
            ? {
                "@context": "https://schema.org",
                "@type": "ItemList",
                name: "Cambridge Cardiovascular Prevention Clinic — Journal",
                itemListElement: items.slice(0, 20).map((it, i) => ({
                  "@type": "ListItem",
                  position: i + 1,
                  item: articleJsonLd({
                    headline: it.title,
                    description: it.summary || it.description,
                    url: it.link || "https://camcpc.co.uk/blog",
                    datePublished: it.published || it.fetched_at,
                    image: it.image,
                    sourceName: it.source,
                    sourceUrl: it.link,
                  }),
                })),
              }
            : undefined
        }
      />
      <section className="bg-cream-100 border-b border-bronze-light/30">
        <div className="container-ccc py-16 max-w-4xl">
          <div className="eyebrow flex items-center gap-2"><Newspaper size={14} weight="duotone" /> Journal · updated daily</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-4 leading-[1.05]">
            Heart & cardiometabolic <em className="text-bronze-dark not-italic">news, in plain English.</em>
          </h1>
          <p className="mt-6 text-ink-muted text-lg leading-relaxed max-w-3xl">
            We follow the journals, trials and news our team reads every week — and rewrite each headline into a short, plain-English summary. Every item links back to the original source so you can read the full piece if you want.
          </p>
        </div>
      </section>

      <section className="container-ccc py-16">
        {error && (
          <div data-testid="journal-error" className="text-sm text-ink-muted p-6 bg-white border border-stone-200 rounded-md">
            The news feed is temporarily unavailable. Please check back shortly.
          </div>
        )}
        {!error && items === null && (
          <div data-testid="journal-loading" className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[...Array(6)].map((_, i) => (
              <div key={`skel-${i}`} className="bg-white border border-stone-200/60 rounded-md overflow-hidden animate-pulse">
                <div className="aspect-[16/10] w-full bg-cream-100" />
                <div className="p-6">
                  <div className="h-3 w-24 bg-cream-100 rounded" />
                  <div className="mt-4 h-5 w-full bg-cream-100 rounded" />
                  <div className="mt-2 h-5 w-4/5 bg-cream-100 rounded" />
                  <div className="mt-6 h-3 w-full bg-cream-100 rounded" />
                  <div className="mt-2 h-3 w-11/12 bg-cream-100 rounded" />
                  <div className="mt-2 h-3 w-3/4 bg-cream-100 rounded" />
                </div>
              </div>
            ))}
          </div>
        )}
        {!error && items && items.length === 0 && (
          <div data-testid="journal-empty" className="text-sm text-ink-muted p-6 bg-white border border-stone-200 rounded-md">
            Nothing new right now — please check back tomorrow.
          </div>
        )}
        {!error && items && items.length > 0 && (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {items.map((it, idx) => {
              const image = it.image || pickFallback(idx);
              return (
                <a
                  key={it.id}
                  href={it.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid={`journal-card-${it.id}`}
                  className="group bg-white border border-stone-200/60 rounded-md overflow-hidden hover:-translate-y-1 hover:shadow-md transition-all flex flex-col"
                >
                  <div className="aspect-[16/10] overflow-hidden bg-cream-100">
                    <img
                      src={image}
                      alt=""
                      loading="lazy"
                      className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700"
                      onError={(e) => {
                        // Swap once to a different-slot fallback and set a guard
                        // so we never re-enter this handler for the same img.
                        // stride 5 is genuinely co-prime with 6 (gcd=1) so any
                        // future pool-size tweak still lands on a distinct slot.
                        const img = e.currentTarget;
                        if (img.dataset.fallbackApplied === "1") return;
                        img.dataset.fallbackApplied = "1";
                        img.src = pickFallback(idx + 5);
                      }}
                    />
                  </div>
                  <div className="p-6 flex-1 flex flex-col">
                    <div className="eyebrow text-bronze-dark/80 flex items-center justify-between gap-2">
                      <span className="truncate">{it.source}</span>
                      {formatDate(it.published_at) && (
                        <span className="text-ink-muted/70 shrink-0">{formatDate(it.published_at)}</span>
                      )}
                    </div>
                    <h3 className="font-serif text-xl mt-3 leading-tight group-hover:text-bronze-dark transition-colors">
                      {it.title}
                    </h3>
                    <p className="mt-3 text-sm text-ink-muted leading-relaxed flex-1">
                      {it.summary}
                    </p>
                    <div className="mt-5 inline-flex items-center gap-1 text-sm text-bronze-dark font-sans">
                      Read the original at {it.source}
                      <ArrowUpRight size={14} weight="bold" />
                    </div>
                  </div>
                </a>
              );
            })}
          </div>
        )}
        {!error && items && items.length > 0 && (
          <p className="mt-10 text-xs text-ink-muted max-w-3xl">
            Summaries are AI-assisted rewrites of publicly-available news, intended for patients. They are not medical advice — for anything about your own care, please speak to your consultant or GP. Original articles are the sole property of their publishers.
          </p>
        )}
      </section>
    </div>
  );
}
